import type {
  BoardProps,
  ItemsPaletteProps,
} from '@cloudscape-design/board-components';
import HelpPanel from '@risk-smart/themed-cloudscape-components/help-panel';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ActionsButton from 'src/components/actions-button';
import type { ActionItem } from 'src/components/actions-button/ActionsButton';
import { useAggregation } from 'src/hooks/useAggregation';
import { useIsModuleEnabledLazy } from 'src/hooks/useIsModuleEnabled';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { useHasPermissionLazy } from 'src/rbac/useHasPermissionLazy';

import type { ObjectToolbarConfig } from '@/blocks/object-toolbar';
import { useUpdateDashboard } from '@/hooks/mutations/dashboard';
import { useGetDashboardById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useUpdateResultNotification } from '@/hooks/useMutationResultNotification';
import { handleError } from '@/utils/errorUtils';

import { defaultDashboardFilter } from '../../context/defaultDashboardFilter';
import { processWidgets } from '../../context/processWidgets';
import { buildDashboardToolbarConfig } from './buildDashboardToolbarConfig';
import DashboardViewToggle from './dashboard-view-toggle/DashboardViewToggle';
import { DashboardFilterRow } from './DashboardFilterRow';
import { DashboardFiltersAlert } from './DashboardFiltersAlert';
import { defaultOverallDashboardLayout } from './defaultLayout';
import { defaultValues } from './forms/dashboardSchema';
import { useContinuousScoring } from './hooks/useContinuousScoring';
import { useGetZip } from './hooks/useGetZip';
import ItemPalette from './item-palette';
import LayoutBoard from './layout-board';
import { SaveDashboardModal } from './SaveDashboardModal';
import type { StoredWidgetDefinition, WidgetDefinition } from './types';
import type {
  DashboardFilter,
  OverallDashboardState,
} from './useDashboardStore';
import {
  useDashboardActions,
  useDashboardFilters,
  useDashboardId,
  useDashboardWidgets,
} from './useDashboardStore';
import { getWidgets } from './widgets';
import {
  filterWidgetsByUserPermissions,
  getPaletteItems,
} from './widgets/utils';

type GetDashboardByIdData = NonNullable<
  ReturnType<typeof useGetDashboardById>['data']
>;

export type Dashboard = GetDashboardByIdData['dashboard_by_pk'];

const OverallDashboard: FC = () => {
  const hasPermission = useHasPermissionLazy();
  const isModuleEnabled = useIsModuleEnabledLazy();
  const { t } = useTranslation(['common'], { keyPrefix: 'dashboard' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const [, setToolsContent] = useTools();
  const [openSaveDashboard, setOpenSaveDashboard] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(false);
  const { updateDashboard } = useUpdateDashboard();
  const widgetList = getWidgets();
  const selectedDashboardId = useDashboardId();
  const filters = useDashboardFilters();
  const widgets = useDashboardWidgets();
  const { setFilters, setWidgets, setDashboardPreferences } =
    useDashboardActions();
  const { getZip } = useGetZip();
  const { addNotification } = useNotifications();
  const { riskModel } = useAggregation();
  const isContinuousScoring = useContinuousScoring();

  const { data: currentDashboardData } = useGetDashboardById({
    queryArgs: { id: selectedDashboardId ?? '' },
    shouldSkip: !selectedDashboardId,
  });
  const currentDashboard = currentDashboardData?.dashboard_by_pk;

  const filtersEnabledCount = useMemo(() => {
    let count = 0;
    count += filters.departments.length;
    count += filters.tags.length;
    if (filters.dateRange) {
      count++;
    }

    return count;
  }, [filters]);

  const resetFilters = () => setFilters(defaultDashboardFilter);

  const getFilteredWidgets = useCallback(() => {
    const definitions: BoardProps.Item<StoredWidgetDefinition>[] = [];
    for (const widget of filterWidgetsByUserPermissions({
      widgets: widgetList,
      widgetsToFilter: widgets,
      hasPermission,
      riskModel,
      isModuleEnabled,
    })) {
      if (!widgetList[widget.widgetType]) {
        handleError(`Unable to find widget with type ${widget.widgetType}`);
        continue;
      }
      const definition = {
        ...widget,
        data: {
          widgetType: widget.widgetType,
          ...widgetList[widget.widgetType],
        },
        definition: widgetList[widget.widgetType].definition,
      };
      definitions.push(definition);
    }

    return definitions;
  }, [hasPermission, isModuleEnabled, riskModel, widgets, widgetList]);

  const [paletteItems, setPaletteItems] = useState<
    ItemsPaletteProps.Item<WidgetDefinition>[]
  >(() =>
    getPaletteItems({
      widgets: widgetList,
      items: getFilteredWidgets(),
      hasPermission,
      riskModel,
      isModuleEnabled,
      isContinuousScoring,
    })
  );

  useEffect(() => {
    setPaletteItems(
      getPaletteItems({
        widgets: widgetList,
        items: getFilteredWidgets(),
        hasPermission,
        riskModel,
        isModuleEnabled,
        isContinuousScoring,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskModel, isModuleEnabled, isContinuousScoring]);

  const onFilterChange = (newFilters: DashboardFilter) => {
    setFilters(newFilters);
  };

  const changeDashboard = useCallback(
    (dashboardToLoad?: OverallDashboardState) => {
      const newItems = filterWidgetsByUserPermissions({
        widgets: widgetList,
        widgetsToFilter: dashboardToLoad
          ? processWidgets(dashboardToLoad.widgets)
          : defaultOverallDashboardLayout,
        hasPermission,
        riskModel,
        isModuleEnabled,
      });

      setDashboardPreferences({
        id: dashboardToLoad?.id,
        filters: dashboardToLoad
          ? dashboardToLoad.filters
          : defaultDashboardFilter,
        widgets: newItems,
      });

      setPaletteItems(
        getPaletteItems({
          widgets: widgetList,
          items: newItems.map((ni) => ({
            ...ni,
            data: { widgetType: ni.widgetType, ...widgetList[ni.widgetType] },
            definition: widgetList[ni.widgetType].definition,
          })),
          hasPermission,
          riskModel,
          isModuleEnabled,
          isContinuousScoring,
        })
      );
    },
    [
      isContinuousScoring,
      hasPermission,
      isModuleEnabled,
      riskModel,
      setDashboardPreferences,
      widgetList,
    ]
  );

  const saveDashboard = useUpdateResultNotification({
    entityName: 'Dashboard',
    asyncAction: async () => {
      if (!currentDashboard) {
        console.error('missing dashboard ID when trying to update dashboard');

        return false;
      }
      await updateDashboard({
        Id: currentDashboard.Id,
        Name: currentDashboard.Name,
        Description: currentDashboard.Description,
        Sharing: currentDashboard.Sharing ?? defaultValues.sharing,
        Content: {
          filters,
          widgets: filterWidgetsByUserPermissions({
            widgets: widgetList,
            widgetsToFilter: widgets,
            hasPermission,
            riskModel,
            isModuleEnabled,
          }),
        },
        ContributorUserIds: currentDashboard.contributors.map((c) => c.UserId),
        ContributorGroupIds: currentDashboard.contributorGroups.map(
          (c) => c.UserGroupId
        ),
      });

      return true;
    },
  });

  const { hasPermission: canUpdate, loading: canUpdateLoading } =
    useHasPermissionQuery('update:dashboard', currentDashboard ?? undefined);

  const actionsButtonItems = useMemo<ActionItem[]>(
    () => [
      {
        text: t('actions.addWidget'),
        id: 'add_widget',
        onItemClick: () => setToolsContent('page-content'),
      },
      {
        text: t('actions.new'),
        id: 'reset_dashboard',
        onItemClick: changeDashboard,
      },
      ...(canUpdate && !canUpdateLoading
        ? [
            {
              text: t('actions.save'),
              id: 'save_dashboard',
              onItemClick: async () => {
                if (selectedDashboardId) {
                  await saveDashboard({});
                } else {
                  setOpenSaveDashboard(true);
                }
              },
            },
          ]
        : []),
      {
        text: t('actions.saveAs'),
        id: 'save_as_dashboard',
        onItemClick: () => setOpenSaveDashboard(true),
      },
      ...(canUpdate && !canUpdateLoading
        ? [
            {
              text: t('actions.edit'),
              id: 'edit_dashboard',
              onItemClick: () => {
                setOpenSaveDashboard(true);
                setEditingDashboard(true);
              },
            },
          ]
        : []),
      {
        text: t('actions.clear'),
        id: 'clear',
        onItemClick: () =>
          changeDashboard({
            id: currentDashboard?.Id,
            filters: {
              tags: [],
              departments: [],
              dateRange: null,
            },
            widgets: [],
          }),
      },
      {
        text: t('actions.exportZip'),
        id: 'export_dashboard_images',
        onItemClick: async () => {
          try {
            addNotification({
              type: 'success',
              content: t('actions.exportZipStarted'),
            });
            await getZip();
          } catch (error) {
            handleError(error);
            addNotification({
              type: 'error',
              content: t('actions.exportZipFailed'),
            });
          }
        },
      },
    ],
    [
      addNotification,
      canUpdate,
      canUpdateLoading,
      changeDashboard,
      currentDashboard,
      getZip,
      saveDashboard,
      selectedDashboardId,
      setToolsContent,
      t,
    ]
  );

  // Object-level toolbar (CUS-174) — flag-gated. `headerOverride` replaces the
  // whole PageLayout header, so the dashboard's controls are re-homed onto the
  // toolbar: the actions menu → overflow ⋮, export → the "Download" group,
  // DashboardViewToggle → the leading tray via `primaryContent`, and the filter
  // dropdowns (DashboardSelector + Filters) plus the active-filters alert → the
  // full-width `banner` row below the bar. The legacy header (below) remains the
  // flag-off fallback and is untouched.
  const toolbarConfig = useMemo<ObjectToolbarConfig>(
    () =>
      buildDashboardToolbarConfig({
        items: actionsButtonItems,
        title: t('overall_page_title'),
        groupLabel: t('dashboardGroup'),
      }),
    [actionsButtonItems, t]
  );

  return (
    <PageLayout
      helpTranslationKey={'dashboard.help'}
      title={t('overall_page_title')}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar
            config={toolbarConfig}
            primaryContent={<DashboardViewToggle />}
            banner={
              <>
                <DashboardFiltersAlert
                  count={filtersEnabledCount}
                  onClear={resetFilters}
                />
                <DashboardFilterRow
                  variant={'toolbar'}
                  onDashboardChange={changeDashboard}
                  selectedDashboardId={selectedDashboardId}
                  onFilterChange={onFilterChange}
                />
              </>
            }
          />
        ) : undefined
      }
      secondary={
        <DashboardFilterRow
          variant={'legacy'}
          onDashboardChange={changeDashboard}
          selectedDashboardId={selectedDashboardId}
          onFilterChange={onFilterChange}
        />
      }
      actions={
        <SpaceBetween size={'s'} direction={'horizontal'}>
          <div className={'flex gap-3 items-center'}>
            <DashboardFiltersAlert
              count={filtersEnabledCount}
              onClear={resetFilters}
            />
            <DashboardViewToggle />
            <ActionsButton
              testId={'dashboardMenu'}
              items={actionsButtonItems}
              buttonText={t('actions_button')}
              variant={'normal'}
            />
          </div>
        </SpaceBetween>
      }
      panelContent={
        <HelpPanel header={t('side_panel_title')}>
          <ItemPalette items={paletteItems} />
        </HelpPanel>
      }
    >
      {openSaveDashboard && (
        <SaveDashboardModal
          onDismiss={() => {
            setOpenSaveDashboard(false);
            setEditingDashboard(false);
          }}
          dashboardContent={{ widgets, filters }}
          isEditing={editingDashboard}
          onDelete={changeDashboard}
        />
      )}
      <LayoutBoard
        items={getFilteredWidgets()}
        onAddWidgetClick={() => setToolsContent('page-content')}
        onItemsChanged={(items) => {
          setWidgets(
            items.map((item) => {
              const widget = widgets.find((w) => item.id === w.id);

              return {
                id: item.id,
                widgetType: item.data.widgetType,
                ...widget,
                rowSpan: item.rowSpan,
                columnSpan: item.columnSpan,
                columnOffset: item.columnOffset,
              };
            })
          );
          setPaletteItems(
            getPaletteItems({
              widgets: widgetList,
              items,
              hasPermission,
              riskModel,
              isModuleEnabled,
              isContinuousScoring,
            })
          );
        }}
      />
    </PageLayout>
  );
};

export default OverallDashboard;
